import React, { useEffect, useState } from "react";
import apiService from "../../service/Api";
import { Box, Card, Typography } from "@mui/material";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import CloudIcon from "@mui/icons-material/Cloud";
import GrainIcon from "@mui/icons-material/Grain";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import FlashOnIcon from "@mui/icons-material/FlashOn";

interface WeatherProps {
  service_id: number;
}

interface WeatherData {
  city: string;
  temperature: number;
  weather: string;
  icon: string;
}

const Weather: React.FC<WeatherProps> = ({ service_id }) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await apiService.get<WeatherData>(
          "/api/management/weather/",
          {
            service_id: service_id,
          }
        );
        setWeather(response.data);
      } catch (err: any) {
        setError(err.response?.data?.error || "Failed to load weather");
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [service_id]);

  if (loading) return <div>Loading weather...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!weather) return null;

  // Map weather conditions to Material UI icons
  const getWeatherIcon = (iconCode: string) => {
    const weatherType = iconCode.slice(0, 2); // Use first two chars of icon code (e.g., '01' for clear, '02' for clouds)
    switch (weatherType) {
      case "01":
        return <WbSunnyIcon sx={{ fontSize: 60, color: "#FFD700" }} />;
      case "02":
      case "03":
      case "04":
        return <CloudIcon sx={{ fontSize: 60, color: "#B0BEC5" }} />;
      case "09":
      case "10":
        return <GrainIcon sx={{ fontSize: 60, color: "#4FC3F7" }} />;
      case "11":
        return <FlashOnIcon sx={{ fontSize: 60, color: "#FFCA28" }} />;
      case "13":
        return <AcUnitIcon sx={{ fontSize: 60, color: "#B3E5FC" }} />;
      default:
        return <CloudIcon sx={{ fontSize: 60, color: "#B0BEC5" }} />;
    }
  };

  return (
    <Box>
      <Card
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 2,
          boxShadow: 0,
          width: "fit-content",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {weather.city}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {weather.weather}
          </Typography>
          <Typography variant="h5" fontWeight="medium">
            {weather.temperature}°C
          </Typography>
        </Box>

        <Box ml={2}>{getWeatherIcon(weather.icon)}</Box>
      </Card>
    </Box>
  );
};

export default Weather;